/**
 * Products Mode — Product catalog with infinite scroll waterfall
 * Dual role:
 *   1. Standalone browsing mode (registered with router as 'products')
 *   2. Product picker for Design mode (called via pickProduct())
 *
 * Ported from Designer's products/index.js
 */

import { registerMode, setMode } from '../../core/router.js'
import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { CONFIG } from '../../core/config.js'
import { createLogger } from '../../utils/logger.js'
import { writeRefAttribution } from '../../utils/ref-attribution.js'
import { getSessionId } from '../../api/index.js'
import { renderEmptyState, renderLoadingSkeleton } from '../page-frame/page-frame.js'


const log = createLogger('products')

// Module state
const state = {
  products: [],
  loading: false,
  hasMore: true,
  offset: 0,
  columns: 2,
  columnHeights: [0, 0],
  scrollListener: null,
}

// Products is a full-width surface (right panel collapsed) — the column count
// must follow the grid width, or a hardcoded 2 columns stretches into a sparse
// two-column layout. why → 2026-07-05 UI review (same fix as the gallery).
function computeProductColumns() {
  const grid = document.getElementById('productsGrid')
  const w = grid?.clientWidth || window.innerWidth || 0
  if (w < 760) return 2
  if (w < 1100) return 3
  if (w < 1440) return 4
  return 5
}

// Recount columns after the grid width settles — entering products collapses
// the right panel with no window event, so a ResizeObserver (not window.resize)
// is what catches the widen. Mirrors gallery-waterfall.js.
let productsResizeObserver = null
function observeProductsGridResize() {
  const grid = document.getElementById('productsGrid')
  if (!grid || productsResizeObserver) return
  let raf = 0
  productsResizeObserver = new ResizeObserver(() => {
    cancelAnimationFrame(raf)
    raf = requestAnimationFrame(() => {
      if (computeProductColumns() !== state.columns) renderProducts(false)
    })
  })
  productsResizeObserver.observe(grid)
}

// ============================================================
// Infinite Scroll
// ============================================================

function setupInfiniteScroll(gridEl) {
  if (!gridEl) return

  if (state.scrollListener) {
    gridEl.removeEventListener('scroll', state.scrollListener)
  }

  let ticking = false
  state.scrollListener = () => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      ticking = false
      if (state.loading || !state.hasMore) return

      const { scrollTop, scrollHeight, clientHeight } = gridEl
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        log.info('Reached scroll threshold, loading more products...')
        loadProducts(true)
      }
    })
  }

  gridEl.addEventListener('scroll', state.scrollListener, { passive: true })
}

// ============================================================
// Skeleton
// ============================================================

function createSkeleton() {
  return `<div class="skeleton-product" style="margin-bottom:12px;">
    <div class="skeleton-image"></div>
    <div class="skeleton-text"></div>
  </div>`
}

// ============================================================
// Load Products
// ============================================================

async function loadProducts(append = false) {
  if (state.loading) return
  if (!append && state.offset > 0) return
  if (append && !state.hasMore) return

  if (!append) {
    state.offset = 0
    state.hasMore = true
    state.columnHeights = [0, 0]
  }

  state.loading = true
  const grid = document.getElementById('productsGrid')
  const sessionId = getSessionId()

  if (!append && grid) {
    grid.innerHTML = `
      <div class="waterfall-container">
        <div class="waterfall-column">${createSkeleton().repeat(3)}</div>
        <div class="waterfall-column">${createSkeleton().repeat(3)}</div>
      </div>
    `
  } else if (append && grid) {
    // Unified loading state (H-130): shimmer skeleton, never a spinner.
    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'loading-indicator'
    loadingDiv.id = 'loadingMoreProducts'
    loadingDiv.innerHTML = renderLoadingSkeleton({ count: 2 })
    grid.appendChild(loadingDiv)
  }

  try {
    const url = `${CONFIG.FRONTEND_API}/api/customer/base-products?scene=designer&limit=20&offset=${state.offset}&session_id=${sessionId}`
    log.info('Fetching products...', { offset: state.offset, append })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000)

    const response = await fetch(url, { signal: controller.signal })
    clearTimeout(timeoutId)

    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    const result = await response.json()

    if (result.success && result.products?.length > 0) {
      const products = result.products
      state.products = append ? state.products.concat(products) : products
      state.offset += products.length
      state.hasMore = result.has_more !== false && products.length >= 20

      log.info(`Loaded ${products.length} products (total: ${state.products.length})`)

      const loadingMore = document.getElementById('loadingMoreProducts')
      if (loadingMore) loadingMore.remove()

      renderProducts(append)

      if (state.hasMore && state.products.length - state.offset < 10) {
        setTimeout(() => loadProducts(true), 1000)
      }

      return state.products
    } else {
      state.hasMore = false
      if (!append && grid) {
        grid.innerHTML = renderEmptyState({
          icon: 'ph-t-shirt',
          message: 'No products available',
        })
      }
      log.warn('No products returned')
      return []
    }
  } catch (error) {
    log.error('Failed to load products:', error)

    const isTimeout = error.name === 'AbortError'
    const errorMessage = isTimeout
      ? 'Request timeout. Please check your connection.'
      : 'Failed to load products'

    if (grid) {
      // Unified empty/error state (H-130); the Retry id is the bind contract below.
      grid.innerHTML = renderEmptyState({
        icon: 'ph-warning',
        message: errorMessage,
        actionLabel: 'Retry',
        actionId: 'productsRetryBtn',
      })
      const retryBtn = document.getElementById('productsRetryBtn')
      if (retryBtn) retryBtn.onclick = () => { state.loading = false; loadProducts(false) }
    }

    if (isTimeout && !append) {
      setTimeout(() => { state.loading = false; loadProducts(false) }, 2000)
    }

    return []
  } finally {
    state.loading = false
  }
}

// ============================================================
// Render Products (waterfall)
// ============================================================

function renderProducts(append = false) {
  const grid = document.getElementById('productsGrid')
  if (!grid) return

  const currentProductId = store.getState('design.currentProduct')?.id

  if (!append) {
    state.columns = computeProductColumns()
    grid.innerHTML = ''
    const container = document.createElement('div')
    container.className = 'waterfall-container'
    container.id = 'productsWaterfallContainer'

    for (let i = 0; i < state.columns; i++) {
      const column = document.createElement('div')
      column.className = 'waterfall-column'
      column.id = `products-col-${i}`
      container.appendChild(column)
    }

    grid.appendChild(container)
    state.columnHeights = new Array(state.columns).fill(0)
    observeProductsGridResize()
  }

  const startIdx = append
    ? state.products.length - (state.offset > 20 ? 20 : state.offset)
    : 0

  for (let idx = startIdx; idx < state.products.length; idx++) {
    const product = state.products[idx]
    const imageUrl =
      product.thumbnail ||
      product.thumbnail_url ||
      product.main_image_url ||
      product.url ||
      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="200"%3E%3Crect fill="%23f5f5f5" width="150" height="200"/%3E%3C/svg%3E'
    const productId = product.sds_id
    const productName = product.sds_par_english_name || 'Product'
    const isActive = String(currentProductId) === String(productId)

    const item = document.createElement('div')
    item.className = 'product-item' + (isActive ? ' active' : '')
    item.dataset.productId = String(productId)
    item.onclick = () => openInDesign(product)

    if (product.reason) {
      const badge = document.createElement('div')
      badge.className = 'rec-badge'
      badge.textContent = getRecommendLabel(product.reason)
      item.appendChild(badge)
    }

    const img = document.createElement('img')
    img.src = imageUrl
    img.loading = idx < 12 ? 'eager' : 'lazy'
    img.alt = productName

    let errorHandled = false
    img.onerror = function () {
      if (!errorHandled) {
        errorHandled = true
        this.src =
          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="200"%3E%3Crect fill="%23f5f5f5" width="150" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E'
      }
    }

    item.appendChild(img)

    const shortestColIndex = state.columnHeights.indexOf(Math.min(...state.columnHeights))
    const column = document.getElementById(`products-col-${shortestColIndex}`)

    if (column) {
      column.appendChild(item)
      state.columnHeights[shortestColIndex] += 200 + 12
    }
  }

  log.info(`Rendered ${state.products.length} products in waterfall layout`)
}

// ============================================================
// Product Selection
// ============================================================

function selectProduct(product) {
  const productId = product.sds_id
  log.info('Product selected:', productId)

  // Update active state in grid
  document.querySelectorAll('.product-item').forEach(el => {
    el.classList.toggle('active', el.dataset.productId === String(productId))
  })

  // Emit event — Design mode listens for this
  eventBus.emit(EVENTS.PRODUCT_SELECT, {
    id: productId,
    name: product.sds_par_english_name || 'Product',
    price: product.current_price || product.price || product.mms_back_end_price_usd,
    variants: product.variants,
    thumbnail: product.thumbnail || product.thumbnail_url || product.main_image_url,
  })
}

/**
 * H-140 products entry: a card click carries the action-pipeline contract
 * ?mode=design&design_blank={sds_id}&ref=products. In-page it reuses the
 * PRODUCT_SELECT (pickProduct) pathway for the blank preselect — the mode
 * switch fires FIRST so the design listener's startDesign runs on the emit —
 * and the URL is reflected for deep-link parity while ref lands in the
 * attribution cookie. External (storefront) entries use the URL directly.
 */
function openInDesign(product) {
  writeRefAttribution('products')
  try {
    const url = new URL(window.location)
    url.searchParams.set('mode', 'design')
    url.searchParams.set('design_blank', String(product.sds_id))
    url.searchParams.set('ref', 'products')
    window.history.replaceState(null, '', url)
  } catch {
    /* non-DOM context — the URL reflection is best-effort */
  }
  setMode('design')
  selectProduct(product)
}

function getRecommendLabel(reason) {
  const labels = {
    personalized: 'FOR YOU',
    trending: 'TRENDING',
    similar: 'SIMILAR',
    explore: 'NEW',
    popular: 'POPULAR',
  }
  return labels[reason] || 'REC'
}

// ============================================================
// Mode Lifecycle
// ============================================================

function activate() {
  log.info('Activating Products mode')
  const container = document.getElementById('mode-products')
  if (!container) return

  // Create DOM structure if not yet present. No per-mode header here — the
  // unified PageHeader (H-130, shared .stage-header) carries title/subtitle.
  if (!document.getElementById('productsGrid')) {
    container.innerHTML = `
      <div class="ws-products__grid" id="productsGrid"></div>
      <div class="ws-products__sentinel" id="productsSentinel"></div>
    `
  }

  const grid = document.getElementById('productsGrid')
  if (grid) {
    setupInfiniteScroll(grid)
  }

  // Load products if not yet loaded
  if (state.products.length === 0) {
    loadProducts(false)
  }
}

function deactivate() {
  log.info('Deactivating Products mode')
}

// Register with router
registerMode('products', { activate, deactivate })

// Also load products when Design mode shows product picker (right panel "products" tab)
eventBus.on(EVENTS.MODE_ACTIVATED, (mode) => {
  if (mode === 'design') {
    // If design mode activates and products grid exists in right panel, load there too
    const rpGrid = document.getElementById('productsGrid')
    if (rpGrid && state.products.length === 0) {
      setupInfiniteScroll(rpGrid)
      loadProducts(false)
    }
  }
})

// Expose for backward compat
if (typeof window !== 'undefined') {
  window.ProductManager = {
    state,
    loadProducts,
    selectProduct,
  }
}

export { loadProducts, selectProduct, state }
export default { loadProducts, selectProduct, state }

log.info('Products mode registered')
